/**
 * OpportunityBrief — advisory cross-topic opportunity/risk lens (RFC-0005 Phase C).
 *
 * A brief synthesises three perspectives on a single topic — the current law,
 * the draft/regulatory trajectory, and gathered market/VOC intel — into an
 * advisory opportunity/risk thesis. It is ADVISORY ONLY and NEVER a verdict
 * (ADR-0002): `isVerdict` is a locked literal `false` so the UI cannot present
 * it as a compliance determination.
 *
 * Citations follow cite-or-omit: every string in `*.citations` / `sampleEvidence`
 * must come from the gathered context that produced the brief — no fabricated
 * regulatory literals or invented sources. Array fields default to `[]`
 * (via `.default(() => [])` factories, so each parse gets a fresh array) so a
 * partially-populated brief still parses instead of being silently dropped.
 */

import { z } from "zod";

export const OpportunityBriefSchema = z.object({
  topicId: z.string().min(1),
  domainId: z.string().min(1),
  label: z.object({ en: z.string().min(1), zh: z.string().min(1) }),
  currentLaw: z.object({
    ruleIds: z.array(z.string()).default(() => []),
    strictness: z.enum(["strict", "moderate", "light", "none"]),
    summary: z.string(),
    citations: z.array(z.string()).default(() => []),
  }),
  draftTrajectory: z.object({
    signalIds: z.array(z.string()).default(() => []),
    direction: z.enum(["tightening", "loosening", "unclear", "new", "none"]),
    summary: z.string(),
    citations: z.array(z.string()).default(() => []),
  }),
  intel: z.object({
    signalIds: z.array(z.string()).default(() => []),
    painThemes: z.array(z.string()).default(() => []),
    intensity: z.enum(["high", "med", "low", "none"]),
    sampleEvidence: z.array(z.string()).default(() => []),
  }),
  opportunity: z.object({
    type: z.enum(["opportunity", "risk", "watch", "none"]),
    // RFC-0008 — content-anchored diagnosis: which way is the regulation a
    // blocker for this market pain. `regulatory_squeeze` = market ↔ mandatory
    // floor conflict (risk); `riding_loosening` = pain from a strict requirement
    // a draft is loosening (opportunity); `compliance_headroom` = the law already
    // PERMITS a fix the brand may not use — "自造痛点" (opportunity; ignore → it
    // becomes a competitive risk). Optional so older briefs + the honest mock
    // (which leaves it unset) still parse.
    diagnosis: z
      .enum(["regulatory_squeeze", "riding_loosening", "compliance_headroom", "watch", "none"])
      .optional(),
    thesis: z.object({ en: z.string(), zh: z.string() }),
    confidence: z.enum(["high", "med", "low"]),
    reasoningTrace: z.string().default(""),
  }),
  isVerdict: z.literal(false),
  model: z.string().min(1),
  generatedAt: z.string().min(1),
});
export type OpportunityBrief = z.infer<typeof OpportunityBriefSchema>;
